import api from './config';

/**
 * API Client para Gestión Documental de Empleados
 *
 * Sistema completo de documentos con:
 * - 11 categorías organizadas
 * - Versionamiento completo
 * - Workflow de aprobación
 * - Control de vencimientos
 * - 4 niveles de confidencialidad
 * - Auditoría de accesos
 */

export const employeeDocumentsAPI = {
  // ═══════════════════════════════════════════════════════════════
  // CATEGORÍAS Y TIPOS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Obtener todas las categorías de documentos (11 categorías)
   */
  getCategorias: async () => {
    const response = await api.get('/employee-documents/categorias');
    return response.data;
  },

  /**
   * Obtener tipos de documentos (opcionalmente filtrados por categoría)
   * @param {string} categoriaId - ID de la categoría (opcional)
   */
  getTipos: async (categoriaId) => {
    const params = categoriaId ? `?categoria=${categoriaId}` : '';
    const response = await api.get(`/employee-documents/tipos${params}`);
    return response.data;
  },

  /**
   * Obtener detalles de un tipo específico
   * @param {string} codigo - Código del tipo de documento
   */
  getTipo: async (codigo) => {
    const response = await api.get(`/employee-documents/tipos/${codigo}`);
    return response.data;
  },

  // ═══════════════════════════════════════════════════════════════
  // CRUD DOCUMENTOS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Listar todos los documentos con filtros opcionales
   * @param {Object} filters - Filtros: employeeId, categoria, tipo, estado, nivel_confidencialidad
   */
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.employeeId) params.append('employeeId', filters.employeeId);
    if (filters.categoria) params.append('categoria', filters.categoria);
    if (filters.tipo) params.append('tipo', filters.tipo);
    if (filters.estado) params.append('estado', filters.estado);
    if (filters.nivel_confidencialidad) params.append('nivel_confidencialidad', filters.nivel_confidencialidad);

    const response = await api.get(`/employee-documents?${params.toString()}`);
    return response.data;
  },

  /**
   * Obtener un documento por ID
   * @param {string} id - ID del documento
   */
  getById: async (id) => {
    const response = await api.get(`/employee-documents/${id}`);
    return response.data;
  },

  /**
   * Obtener todos los documentos de un empleado
   * @param {string} employeeId - ID del empleado
   */
  getEmployeeDocuments: async (employeeId) => {
    const response = await api.get(`/employee-documents/employee/${employeeId}`);
    return response.data;
  },

  /**
   * Crear un nuevo documento (estado: draft)
   * @param {Object} documentData - Datos del documento + archivo
   */
  create: async (documentData) => {
    const formData = new FormData();

    Object.keys(documentData).forEach(key => {
      if (documentData[key] !== null && documentData[key] !== undefined) {
        if (key === 'archivo' && documentData[key] instanceof File) {
          formData.append('archivo', documentData[key]);
        } else if (key === 'datos_documento') {
          formData.append('datos_documento', JSON.stringify(documentData[key]));
        } else {
          formData.append(key, documentData[key]);
        }
      }
    });

    const response = await api.post('/employee-documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Actualizar metadata del documento (sin cambiar archivo)
   * @param {string} id - ID del documento
   * @param {Object} documentData - Datos a actualizar
   */
  update: async (id, documentData) => {
    const response = await api.put(`/employee-documents/${id}`, documentData);
    return response.data;
  },

  /**
   * Archivar un documento (soft delete)
   * @param {string} id - ID del documento
   * @param {string} motivo - Razón del archivado
   */
  archive: async (id, motivo) => {
    const response = await api.delete(`/employee-documents/${id}`, {
      data: { motivo }
    });
    return response.data;
  },

  // ═══════════════════════════════════════════════════════════════
  // WORKFLOW DE APROBACIÓN
  // ═══════════════════════════════════════════════════════════════

  /**
   * Enviar documento a aprobación (draft → pending)
   * @param {string} id - ID del documento
   */
  submit: async (id) => {
    const response = await api.post(`/employee-documents/${id}/submit`);
    return response.data;
  },

  /**
   * Aprobar documento (pending → approved)
   * @param {string} id - ID del documento
   */
  approve: async (id) => {
    const response = await api.post(`/employee-documents/${id}/approve`);
    return response.data;
  },

  /**
   * Rechazar documento (pending → rejected)
   * @param {string} id - ID del documento
   * @param {string} motivo - Razón del rechazo
   */
  reject: async (id, motivo) => {
    const response = await api.post(`/employee-documents/${id}/reject`, { motivo });
    return response.data;
  },

  // ═══════════════════════════════════════════════════════════════
  // VERSIONAMIENTO
  // ═══════════════════════════════════════════════════════════════

  /**
   * Obtener historial de versiones de un documento
   * @param {string} id - ID del documento
   */
  getVersions: async (id) => {
    const response = await api.get(`/employee-documents/${id}/versions`);
    return response.data;
  },

  /**
   * Subir una nueva versión del documento
   * @param {string} id - ID del documento
   * @param {File} file - Nuevo archivo
   * @param {string} motivo - Razón del cambio
   */
  uploadNewVersion: async (id, file, motivo) => {
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('motivo', motivo);

    const response = await api.post(`/employee-documents/${id}/new-version`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // ═══════════════════════════════════════════════════════════════
  // DESCARGAS (con auditoría)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Descargar documento (registra en auditoría)
   * @param {string} id - ID del documento
   */
  download: async (id) => {
    const response = await api.get(`/employee-documents/${id}/download`, {
      responseType: 'blob'
    });
    return response;
  },

  /**
   * Obtener log de descargas de un documento
   * @param {string} id - ID del documento
   */
  getDownloadLog: async (id) => {
    const response = await api.get(`/employee-documents/${id}/download-log`);
    return response.data;
  },

  // ═══════════════════════════════════════════════════════════════
  // ALERTAS Y VENCIMIENTOS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Obtener documentos próximos a vencer (30 días o menos)
   */
  getPendingAlerts: async () => {
    const response = await api.get('/employee-documents/alerts/pending');
    return response.data;
  },

  /**
   * Obtener documentos vencidos
   */
  getExpired: async () => {
    const response = await api.get('/employee-documents/alerts/expired');
    return response.data;
  },

  /**
   * Generar alertas de vencimiento (cron job - solo admin)
   */
  generateAlerts: async () => {
    const response = await api.post('/employee-documents/alerts/generate');
    return response.data;
  },

  // ═══════════════════════════════════════════════════════════════
  // ESTADÍSTICAS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Obtener resumen estadístico general
   */
  getStats: async () => {
    const response = await api.get('/employee-documents/stats/summary');
    return response.data;
  },

  /**
   * Obtener reporte de cumplimiento (documentos obligatorios faltantes)
   */
  getCompliance: async () => {
    const response = await api.get('/employee-documents/stats/compliance');
    return response.data;
  }
};

export default employeeDocumentsAPI;
